// Every numeric value here is persisted (Convex int64 and Postgres integer
// columns): a member's number is data. WordReadingFluencyEnum's order is not
// semantic (Strong = 2 sorts before Learned = 3); do not tidy it.
// value-fidelity.test.ts pins each number.

// Recognition: read fluently, unaided. Identification: the reader needed help.
export const ResponseId = {
  Identification: 0,
  Recognition: 1,
} as const;
export type ResponseId = (typeof ResponseId)[keyof typeof ResponseId];

// Which path earned a response: the tapped word (planTap's target), a word
// passed over before it (planTap's range), a word credited when a page is
// finalized (planPageFinalize), or a word-list answer. Persisted on each
// convex-reader fluencyResponses row.
export const CLIENT_FLUENCY_RESPONSE_SOURCES = [
  "tap",
  "pass_over",
  "page_finalize",
  "word_list",
] as const;
export type ClientFluencyResponseSource =
  (typeof CLIENT_FLUENCY_RESPONSE_SOURCES)[number];

// Kept out of the client tuple so a reader's device can never submit a
// response an adult scored (an aWRF word, a printed worksheet's grade); only
// the server writes them.
export const SERVER_FLUENCY_RESPONSE_SOURCES = ["awrf", "worksheet"] as const;

export const FLUENCY_RESPONSE_SOURCES = [
  ...CLIENT_FLUENCY_RESPONSE_SOURCES,
  ...SERVER_FLUENCY_RESPONSE_SOURCES,
] as const;
export type FluencyResponseSource = (typeof FLUENCY_RESPONSE_SOURCES)[number];

export const WordReadingFluencyEnum = {
  Unknown: 0,
  Initial: 1,
  Strong: 2,
  Learned: 3,
  Developing: 4,
  Predicted: 5,
} as const;
export type WordReadingFluencyEnum =
  (typeof WordReadingFluencyEnum)[keyof typeof WordReadingFluencyEnum];

export const ActionId = {
  UnknownToDeveloping: 0,
  UnknownToInitial: 1,
  PredictedToDeveloping: 2,
  PredictedToInitial: 3,
  InitialToStrong: 4,
  LearnedToStrong: 5,
  DevelopingToLearned: 6,
  InitialToDeveloping: 7,
  LearnedToDeveloping: 8,
  StrongToDeveloping: 9,
  DevelopingToDeveloping: 10,
  LearnedToLearned: 11,
  StrongToStrong: 12,
  InitialToInitial: 13,
  DevelopingToInitial: 14,
  LearnedToInitial: 15,
  StrongToInitial: 16,
  Unknown: 17,
} as const;
export type ActionId = (typeof ActionId)[keyof typeof ActionId];

// Research value (Q3): the 16 most recent responses are what matching reads.
// Client and server must cap at the same point, or getActionId replays a
// different window on each side and reports a divergence that is not one.
export const RESPONSE_HISTORY_CAP = 16;

export const appendResponse = <T>(history: readonly T[], response: T): T[] =>
  [...history, response].slice(-RESPONSE_HISTORY_CAP);

export const MIN_STRONG_WINDOW = 5;
export const MIN_LEARNED_WINDOW = 3;

export type FluencyLevelName =
  | "unknown"
  | "initial"
  | "developing"
  | "learned"
  | "strong"
  | "predicted";

export function getFluencyLevelName(
  level: WordReadingFluencyEnum,
): FluencyLevelName {
  switch (level) {
    case WordReadingFluencyEnum.Unknown:
      return "unknown";
    case WordReadingFluencyEnum.Initial:
      return "initial";
    case WordReadingFluencyEnum.Developing:
      return "developing";
    case WordReadingFluencyEnum.Learned:
      return "learned";
    case WordReadingFluencyEnum.Strong:
      return "strong";
    case WordReadingFluencyEnum.Predicted:
      return "predicted";
  }
}

const levelAfterAction = (actionId: ActionId): WordReadingFluencyEnum => {
  switch (actionId) {
    case ActionId.UnknownToInitial:
    case ActionId.PredictedToInitial:
    case ActionId.InitialToInitial:
    case ActionId.DevelopingToInitial:
    case ActionId.LearnedToInitial:
    case ActionId.StrongToInitial:
      return WordReadingFluencyEnum.Initial;
    case ActionId.LearnedToLearned:
    case ActionId.DevelopingToLearned:
      return WordReadingFluencyEnum.Learned;
    case ActionId.InitialToStrong:
    case ActionId.LearnedToStrong:
    case ActionId.StrongToStrong:
      return WordReadingFluencyEnum.Strong;
    case ActionId.Unknown:
      return WordReadingFluencyEnum.Unknown;
  }
  return WordReadingFluencyEnum.Developing;
};

// Research rule: two fluent reads with no prior slip reach Strong; any slip
// drops to Developing; Learned is reachable only from Developing; a fluent read
// never lowers a level (no Strong -> Learned). The history is replayed from the
// start because the level depends on the path, not just the trailing window.
export const getActionId = ({
  record,
}: {
  record: ResponseId[];
}): ActionId | null => {
  if (record.length < 1) return null;

  let level: WordReadingFluencyEnum = WordReadingFluencyEnum.Unknown;
  let consecutiveRecognitions = 0;
  let actionId: ActionId = ActionId.Unknown;

  for (const response of record) {
    const fluent = response === ResponseId.Recognition;
    consecutiveRecognitions = fluent ? consecutiveRecognitions + 1 : 0;

    switch (level) {
      case WordReadingFluencyEnum.Unknown:
        actionId = fluent
          ? ActionId.UnknownToInitial
          : ActionId.UnknownToDeveloping;
        break;
      case WordReadingFluencyEnum.Initial:
        actionId = fluent
          ? ActionId.InitialToStrong
          : ActionId.InitialToDeveloping;
        break;
      case WordReadingFluencyEnum.Strong:
        actionId = fluent
          ? ActionId.StrongToStrong
          : ActionId.StrongToDeveloping;
        break;
      case WordReadingFluencyEnum.Learned:
        if (!fluent) actionId = ActionId.LearnedToDeveloping;
        else if (consecutiveRecognitions >= MIN_STRONG_WINDOW)
          actionId = ActionId.LearnedToStrong;
        else actionId = ActionId.LearnedToLearned;
        break;
      default:
        // Developing (Predicted is never produced from a response history)
        actionId =
          fluent && consecutiveRecognitions >= MIN_LEARNED_WINDOW
            ? ActionId.DevelopingToLearned
            : ActionId.DevelopingToDeveloping;
        break;
    }
    level = levelAfterAction(actionId);
  }

  return actionId;
};

export const getWordReadingFluencyLevel = (
  target_fluency_record: ResponseId[],
): WordReadingFluencyEnum => {
  if (!target_fluency_record.length) return WordReadingFluencyEnum.Unknown;
  const actionId = getActionId({ record: target_fluency_record });
  return actionId === null
    ? WordReadingFluencyEnum.Unknown
    : levelAfterAction(actionId);
};

// StrongToStrong rows carry no state change and recur often enough in ordinary
// reading to dominate the table. Only the record is suppressed: the caller must
// still append the response to the fluency history, or Strong records never
// refresh their timestamp and ./decay demotes them.
export function shouldCreateRecord(actionId: ActionId | null): boolean {
  if (actionId === null) return false;
  return actionId !== ActionId.StrongToStrong;
}
